import Icon1 from "@assets/icons/Icon";
import Button from "@components/ui/buttons/Button";
import SecondaryButton from "@components/ui/buttons/SecondaryButton";
import { ProjectTemplateModal } from "@components/modals/ProjectTemplateModal";
import SearchField from "@components/ui/textfields/SearchField";
import { Text1, Text2, Text3 } from "@components/ui/texts/Texts";
import { useProjectStore } from "@store/projectStore";
import { useDataStore } from "@store/user_org";
import { useState } from "react";
import { projectTemplatesApi } from "@services/api";
import { showNotification } from "context/notification.service";
import { defaultProjectTemplates } from "./defaultProjectTemplates";

export const ProjectTemplates = () => {
  const projectTemplates = useProjectStore((state) => state.projectTemplates);
  const isLoadingProjectTemplates = useProjectStore((state) => state.isLoadingProjectTemplates);
  const userData = useDataStore((state) => state.userData);
  const [value, setValue] = useState("");
  const [selectedTemplate, setSelectedTemplate] = useState<string | null>(null);
  const [newTemplateOpen, setNewTemplateOpen] = useState(false);
  const [isCreatingDefaults, setIsCreatingDefaults] = useState(false);

  const handleCreateDefaults = async () => {
    if (!userData?.org_id) return;

    setIsCreatingDefaults(true);
    try {
      let created = 0;
      for (const template of defaultProjectTemplates) {
        const result = await projectTemplatesApi.create({
          name: template.name,
          org_id: userData.org_id,
          status: "active",
          themes: template.themes,
          folders: template.folders,
        });
        useProjectStore.getState().setProjectTemplates([
          ...useProjectStore.getState().projectTemplates,
          result,
        ]);
        created++;
      }
      showNotification({
        type: "success",
        message: `${created} modèle(s) par défaut ajouté(s)`,
      });
    } catch (error) {
      console.error("Error creating default project templates:", error);
      showNotification({
        type: "error",
        message: "Erreur lors de la création des modèles",
      });
    } finally {
      setIsCreatingDefaults(false);
    }
  };

  const getTemplateCounts = (template: any) => {
    const themes = template.themes?.length || 0;
    let steps = 0;
    let controlpoints = 0;
    (template.themes || []).forEach((theme: any) => {
      steps += theme.steps?.length || 0;
      (theme.steps || []).forEach((step: any) => {
        controlpoints += step.controlpoints?.length || 0;
      });
    });
    return { themes, steps, controlpoints };
  };

  return (
    <div className="flex flex-col h-fit pb-96 mt-10 mx-4 lg:ml-10">
      {(selectedTemplate || newTemplateOpen) && (
        <ProjectTemplateModal
          templateId={selectedTemplate || ""}
          onClose={() => {
            setSelectedTemplate(null);
            setNewTemplateOpen(false);
          }}
        />
      )}
      <div className="flex flex-col w-full max-w-400 gap-4">
        <div className="flex flex-col">
          <Text1>Modèles de projets</Text1>
          <Text2>Configurez vos modèles de projets ici.</Text2>
          <Text3>
            Les modèles de projets définissent les thèmes, étapes et points de
            contrôle utilisés lors de la création d'un nouveau projet.
          </Text3>
        </div>
      </div>
      <div className="flex flex-col gap-2 mt-4 bg-white max-w-900 p-4 rounded-lg border-1">
        <div className="flex flex-row gap-2 items-center">
          <div className="w-full max-w-400">
            <SearchField
              value={value}
              onChange={(e) => setValue(e.target.value)}
              placeholder="construction, rénovation..."
            />
          </div>
          {projectTemplates.length === 0 && (
            <SecondaryButton
              variant="small"
              startIcon={<Icon1 name="plus" size={14} />}
              onClick={handleCreateDefaults}
              disabled={isCreatingDefaults}
            >
              {isCreatingDefaults
                ? "Création en cours..."
                : "Ajouter les modèles par défaut"}
            </SecondaryButton>
          )}
        </div>

        <div className="flex flex-col divide-y mt-4">
          {projectTemplates
            .filter((template) =>
              template.infos.general.name
                .toLowerCase()
                .includes(value.toLowerCase()),
            )
            .map((template, index) => {
              const counts = getTemplateCounts(template);
              return (
                <div
                  key={index}
                  className="flex flex-row rounded-xl items-center px-4 py-2"
                >
                  <div className="flex flex-col w-250">
                    <div className="text-h2 font-medium">
                      {template.infos.general.name}
                    </div>
                  </div>
                  <div className="text-h3 text-neutral-400 mr-auto flex flex-row gap-3">
                    <span>{counts.themes} thèmes</span>
                    <span>{counts.steps} étapes</span>
                    <span>{counts.controlpoints} points</span>
                  </div>
                  <SecondaryButton
                    label="Modifier"
                    variant="small"
                    onClick={() => setSelectedTemplate(template.id)}
                  />
                </div>
              );
            })}
        </div>

        <Button
          className="mt-4"
          label="Nouveau modèle de projet"
          onClick={() => setNewTemplateOpen(true)}
        />
      </div>
    </div>
  );
};
